// IndexedDB: offline copies of cloud boards, unsent edits, and boards made without an account.
import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { BoardBackground, Op, SepiaElement } from "../../shared/model";

export interface GuestBoard {
  id: string;
  title: string;
  bg: BoardBackground;
  thumb: string | null;
  createdAt: number;
  updatedAt: number;
  elCount: number;
}

interface BoardState {
  key: string;
  rev: number;
  pending: Op[];
  camera?: { x: number; y: number; z: number };
}

interface SepiaDB extends DBSchema {
  els: { key: [string, string]; value: { b: string; id: string; el: SepiaElement }; indexes: { b: string } };
  state: { key: string; value: BoardState };
  guest: { key: string; value: GuestBoard };
  assets: { key: string; value: { id: string; blob: Blob; w: number; h: number } };
}

let dbp: Promise<IDBPDatabase<SepiaDB>> | null = null;

function db(): Promise<IDBPDatabase<SepiaDB>> {
  if (!dbp) {
    dbp = openDB<SepiaDB>("sepia", 1, {
      upgrade(d) {
        const els = d.createObjectStore("els", { keyPath: ["b", "id"] });
        els.createIndex("b", "b");
        d.createObjectStore("state", { keyPath: "key" });
        d.createObjectStore("guest", { keyPath: "id" });
        d.createObjectStore("assets", { keyPath: "id" });
      }
    });
  }
  return dbp;
}

export async function loadEls(board: string): Promise<SepiaElement[]> {
  try {
    const rows = await (await db()).getAllFromIndex("els", "b", board);
    return rows.map((r) => r.el);
  } catch {
    return [];
  }
}

export async function writeEls(board: string, puts: SepiaElement[], dels: string[]): Promise<void> {
  if (!puts.length && !dels.length) return;
  const d = await db();
  const tx = d.transaction("els", "readwrite");
  for (const el of puts) tx.store.put({ b: board, id: el.id, el });
  for (const id of dels) tx.store.delete([board, id]);
  await tx.done;
}

export async function replaceEls(board: string, els: SepiaElement[]): Promise<void> {
  const d = await db();
  const tx = d.transaction("els", "readwrite");
  const keys = await tx.store.index("b").getAllKeys(board);
  for (const k of keys) tx.store.delete(k);
  for (const el of els) tx.store.put({ b: board, id: el.id, el });
  await tx.done;
}

export async function clearBoard(board: string): Promise<void> {
  const d = await db();
  const tx = d.transaction(["els", "state"], "readwrite");
  const keys = await tx.objectStore("els").index("b").getAllKeys(board);
  for (const k of keys) tx.objectStore("els").delete(k);
  tx.objectStore("state").delete(board);
  await tx.done;
}

export async function getState(board: string): Promise<BoardState | undefined> {
  try {
    return await (await db()).get("state", board);
  } catch {
    return undefined;
  }
}

export async function setState(state: BoardState): Promise<void> {
  await (await db()).put("state", state);
}

// ---- boards made without an account ----

export async function listGuestBoards(): Promise<GuestBoard[]> {
  try {
    const all = await (await db()).getAll("guest");
    return all.sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

export async function getGuestBoard(id: string): Promise<GuestBoard | undefined> {
  return (await db()).get("guest", id);
}

export async function putGuestBoard(b: GuestBoard): Promise<void> {
  await (await db()).put("guest", b);
}

export async function deleteGuestBoard(id: string): Promise<void> {
  await clearBoard("g:" + id);
  await (await db()).delete("guest", id);
}

// ---- images on guest boards ----

const urlCache = new Map<string, string>();

export async function putAsset(blob: Blob, w: number, h: number): Promise<string> {
  const id = "local-" + crypto.randomUUID();
  await (await db()).put("assets", { id, blob, w, h });
  urlCache.set(id, URL.createObjectURL(blob));
  return id;
}

export async function getAssetBlob(id: string): Promise<Blob | undefined> {
  return (await (await db()).get("assets", id))?.blob;
}

/** Object URL for a locally stored image (loads it on first use). */
export function localAssetUrl(id: string, onReady: () => void): string {
  const hit = urlCache.get(id);
  if (hit) return hit;
  db()
    .then((d) => d.get("assets", id))
    .then((row) => {
      if (row && !urlCache.has(id)) {
        urlCache.set(id, URL.createObjectURL(row.blob));
        onReady();
      }
    })
    .catch(() => undefined);
  return "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
}
